import { Globe, Smartphone, Megaphone, ShoppingCart, Database, Sparkles, Briefcase, Boxes, Code2, PhoneOutgoing, Headphones, Check, ArrowRight, MessageSquarePlus } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { SERVICES } from "../lib/constants";
import Reveal from "../components/Reveal";
import InquiryModal from "../components/InquiryModal";
import { Toaster } from "sonner";

const ICONS = { Globe, Smartphone, Megaphone, ShoppingCart, Database, Sparkles, Briefcase, Boxes, Code2, PhoneOutgoing, Headphones };

const ACCENTS = {
  web: "#FF6B1A", mobile: "#3B82F6", marketing: "#FFAA33", ecommerce: "#FF6B1A",
  crm: "#3B82F6", ai: "#FFAA33", consult: "#3B82F6", block: "#FF6B1A",
  custom: "#3B82F6", telesales: "#FFAA33", support: "#FF6B1A",
};

const DETAILS = {
  web: ["Next.js / React / Astro", "Headless CMS", "Lighthouse 95+ scores"],
  mobile: ["Flutter & React Native", "iOS / Android native modules", "Push, payments, analytics"],
  marketing: ["SEO & content strategy", "Google / Meta / LinkedIn ads", "Conversion optimisation"],
  ecommerce: ["Shopify / WooCommerce / Custom", "Multi-vendor marketplaces", "ERP & logistics integrations"],
  crm: ["Sales pipelines & automation", "ERP modules: HR, Inventory, Finance", "Integrations with existing systems"],
  ai: ["RAG over private data", "Agentic workflow automation", "LLM cost optimisation"],
  consult: ["Architecture & cloud audits", "Tech-stack selection", "Hiring & team scaling"],
  block: ["Smart contracts (Solidity / Move)", "DApps & wallets", "Token launches & DEX integration"],
  custom: ["Deeply bespoke domain software", "Internal tools at scale", "Legacy modernisation"],
  telesales: ["B2B & B2C outbound teams", "CRM-integrated dialing", "Daily reporting & QA"],
  support: ["24×7 inbound desks", "Multilingual agents", "Tickets in Bengali, Hindi, English"],
};

const PROCESS = [
  { n: "01", t: "Discover", d: "Free 30-min call. We map your goals, constraints and success metrics." },
  { n: "02", t: "Design", d: "Wireframes, system design, and UX prototypes — fully validated before code." },
  { n: "03", t: "Build", d: "Weekly demos. Production-quality engineering with code reviews and testing." },
  { n: "04", t: "Launch", d: "Smooth go-live with monitoring, analytics and a hand-over runbook." },
  { n: "05", t: "Grow", d: "Ongoing optimisation, new features and 24×7 support." },
];

export default function Services() {
  const [activeInquiry, setActiveInquiry] = useState(null);

  return (
    <div data-testid="services-page" className="pt-32 pb-20 relative">
      <Toaster position="top-center" theme="dark" />
      <div className="absolute inset-0 -z-10">
        <div className="orb orb-flame w-[500px] h-[500px] top-0 -left-32 opacity-25" />
        <div className="orb orb-azure w-[600px] h-[600px] top-1/3 -right-40 opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Hero */}
        <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">Services</span></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05]">
            A complete <span className="fb-text">tech stack</span><br />for ambitious teams.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-7 max-w-2xl text-xl text-zinc-300 leading-relaxed">
            Eleven specialised practices, one accountable partner. Pick what you need today, scale up tomorrow.
          </p>
        </Reveal>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-16">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            const accent = ACCENTS[s.key] || "#FF6B1A";
            const bullets = DETAILS[s.key] || [];
            return (
              <Reveal key={s.key} delay={(i % 4) * 0.05}>
                <motion.div
                  whileHover={{ y: -6 }}
                  data-testid={`service-card-${s.key}`}
                  className="group glass rounded-3xl overflow-hidden hover:border-[#FF6B1A]/40 transition-all h-full relative spotlight"
                  onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty('--x', `${e.clientX - r.left}px`); e.currentTarget.style.setProperty('--y', `${e.clientY - r.top}px`); }}
                >
                  {/* Photo header */}
                  <Link to={`/services/${s.key}`} className="block relative aspect-[16/9] overflow-hidden bg-[#1a1a1d]">
                    <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover opacity-65 group-hover:opacity-95 group-hover:scale-110 transition-all duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="w-11 h-11 rounded-2xl flex items-center justify-center backdrop-blur-md" style={{ background: `linear-gradient(135deg, ${accent}66, transparent)`, border: `1px solid ${accent}99` }}>
                        <Icon size={18} style={{ color: "#fff" }} />
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-black/60 border border-white/15 text-zinc-200">Service</span>
                    </div>
                  </Link>

                  <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-0 group-hover:opacity-20 transition-opacity pointer-events-none" style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)`, filter: "blur(40px)" }} />

                  <div className="relative p-7">
                    <h3 className="text-xl font-bold flex items-center gap-2">{s.title}
                      <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" style={{ color: accent }} />
                    </h3>
                    <p className="text-zinc-400 text-sm mt-1.5 mb-4">{s.desc}</p>
                    <ul className="space-y-1.5">
                      {bullets.map((b) => (
                        <li key={b} className="text-sm text-zinc-300 flex items-center gap-2">
                          <Check size={14} style={{ color: accent }} /> {b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button
                        onClick={() => setActiveInquiry({ key: s.key, label: s.title, accent })}
                        data-testid={`service-inquiry-btn-${s.key}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-black transition-all hover:shadow-lg"
                        style={{ background: `linear-gradient(135deg, ${accent}, #FFAA33)` }}
                      >
                        <MessageSquarePlus size={14} /> Get a quote
                      </button>
                      <Link
                        to={`/services/${s.key}`}
                        data-testid={`service-detail-btn-${s.key}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-white/15 hover:bg-white/5 hover:border-[#3B82F6]/60 transition-all"
                      >
                        Details <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </Reveal>
            );
          })}
        </div>

        {/* Process */}
        <div className="mt-28">
          <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#3B82F6] uppercase">How we work</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl font-bold">A simple, <span className="azure-text">battle-tested</span> process.</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-12">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.05}>
                <motion.div whileHover={{ y: -6 }} className="glass rounded-2xl p-6 h-full relative overflow-hidden">
                  <span className="absolute -top-2 -right-1 text-7xl font-bold opacity-10 fb-text">{p.n}</span>
                  <p className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">{p.n}</p>
                  <h3 className="mt-2 text-lg font-semibold">{p.t}</h3>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{p.d}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Reveal>
          <div className="mt-24 gradient-border">
            <div className="rounded-3xl p-10 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold">Not sure where to start?</h2>
              <p className="text-zinc-400 mt-3 max-w-xl mx-auto">Book a free discovery call — our AI assistant Flare can schedule it now.</p>
              <Link to="/contact" className="inline-block mt-7 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] via-[#FFAA33] to-[#3B82F6] text-black font-semibold hover:shadow-[0_0_30px_rgba(255,107,26,0.5)]">Talk to us</Link>
            </div>
          </div>
        </Reveal>
      </div>

      <InquiryModal
        open={!!activeInquiry}
        onClose={() => setActiveInquiry(null)}
        type="service"
        itemKey={activeInquiry?.key}
        itemLabel={activeInquiry?.label}
        accent={activeInquiry?.accent}
      />
    </div>
  );
}
